import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

interface ChatMessage {
  id: string;
  message: string;
  msg_from: 'user' | 'gpt';
  timestamp?: string;
}

interface ModernChatStore {
  chatMessages: ChatMessage[];
  currWebSocket: WebSocket | null;
  pendingResponse: boolean;
  
  clearChatMessages: () => void;
  addToChatMessage: (msg: string, msg_from: 'user' | 'gpt') => void;
  initChatWebSocket: () => void;
  sendChatMessageAsync: (msg: string) => Promise<void>;
}

const getWebSocketUrl = (): string => {
  // Use the same pattern as react-frontend: ApiPath + chat_id
  const ApiPath = 'wss://genie-autogen.azurewebsites.net/ws/';
  const chat_id = uuidv4();
  return ApiPath + chat_id;
};

export const useModernChatStore = create<ModernChatStore>((set, get) => ({
  chatMessages: [],
  currWebSocket: null,
  pendingResponse: false,

  clearChatMessages: () => set({ chatMessages: [] }),
  
  addToChatMessage: (msg: string, msg_from: 'user' | 'gpt') => {
    // Log all incoming messages for debugging
    console.log('Incoming message:', { msg, msg_from });
    
    let cleanedMessage = msg;
    
    // Clean up agent messages by removing BRKT or TERMINATE suffixes
    if (msg_from === 'gpt') {
             // Remove various forms of BRKT/TERMINATE from the end of messages
       cleanedMessage = msg
         .replace(/\s*\[?BRKT\.?\]?\s*$/i, '')         // Remove [BRKT], BRKT, BRKT., or variations at end
         .replace(/\s*\[?TERMINATE\]?\s*$/i, '')       // Remove [TERMINATE], TERMINATE, or variations at end
         .trim();                                      // Clean up any remaining whitespace
      
      // If the message becomes empty after cleaning, don't show it
      if (!cleanedMessage) {
        console.log('Filtered out empty system message after cleaning:', msg);
        return;
      }
      
      // If the original message was significantly different, log the cleanup
      if (cleanedMessage !== msg) {
        console.log('Cleaned message:', { original: msg, cleaned: cleanedMessage });
      }
    }

    set((state) => ({
      chatMessages: [
        ...state.chatMessages,
        {
          id: `${msg_from}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          message: cleanedMessage,
          msg_from: msg_from,
          timestamp: new Date().toISOString(),
        }
      ]
    }));
  },

  initChatWebSocket: () => {
    const chat_id = uuidv4();
    const wsUrl = getWebSocketUrl();
    console.log('Connecting to WebSocket:', wsUrl, 'with chat_id:', chat_id);
    
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = (event) => {
      console.log("WebSocket connected", chat_id);
      set({ currWebSocket: ws });
    };
    
    ws.onmessage = (event) => {
      get().addToChatMessage(event.data, "gpt");
      set({ pendingResponse: false });
    };
    
    ws.onclose = (event) => {
      console.log("WebSocket closed", event);
      set({ currWebSocket: null, pendingResponse: false });
    };
    
    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      set({ pendingResponse: false });
    };
  },

  sendChatMessageAsync: async (msg: string) => {
    try {
      const currentWs = get().currWebSocket;
      if (currentWs && currentWs.readyState === WebSocket.OPEN) {
        get().addToChatMessage(msg, "user");
        currentWs.send(msg);
        set({ pendingResponse: true });
      } else {
        console.log("WebSocket not available or not open");
      }
    } catch (err) {
      console.error("Error sending message:", err);
      set({ pendingResponse: false });
    }
  },
})); 